/**
 * Global error context for managing application-wide error state
 */

import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { ErrorDetails, ErrorCategory, ErrorSeverity, GlobalErrorContextType, ErrorContext as ErrorContextType } from '../types/error';
import { errorLogger } from '../services/errorLogger';
import { createErrorDetails, getRetryDelay } from '../utils/errorUtils';

const ErrorContext = createContext<GlobalErrorContextType | undefined>(undefined);

export const useError = (): GlobalErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: React.ReactNode;
  maxErrors?: number;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ 
  children, 
  maxErrors = 50 
}) => {
  const [errors, setErrors] = useState<ErrorDetails[]>([]);

  // Load persisted logs on mount
  useEffect(() => {
    errorLogger.loadPersistedLogs();
  }, []);

  /**
   * Report a new error to the global error system
   */
  const reportError = useCallback((
    error: Error, 
    context?: Partial<ErrorContextType>
  ): string => {
    const errorDetails = createErrorDetails(error);
    
    // Add any additional context
    if (context) {
      errorDetails.context = { ...errorDetails.context, ...context };
    }

    // Log the error
    errorLogger.logError(errorDetails);

    // Add to local state
    setErrors(prev => {
      const newErrors = [errorDetails, ...prev].slice(0, maxErrors);
      return newErrors;
    });

    return errorDetails.id;
  }, [maxErrors]);

  /**
   * Clear a specific error by ID
   */
  const clearError = useCallback((errorId: string): void => {
    setErrors(prev => prev.filter(error => error.id !== errorId));
  }, []);

  /**
   * Clear all errors
   */
  const clearAllErrors = useCallback((): void => {
    setErrors([]);
  }, []);

  /**
   * Retry a specific error (if recoverable)
   */
  const retryError = useCallback(async (errorId: string): Promise<boolean> => {
    const error = errors.find(e => e.id === errorId);
    if (!error || !error.recoverable) {
      return false;
    }

    const currentRetryCount = error.retryCount || 0;
    const maxRetries = error.maxRetries || 3;

    if (currentRetryCount >= maxRetries) {
      return false;
    }

    // Update retry count
    setErrors(prev => prev.map(e => 
      e.id === errorId 
        ? { ...e, retryCount: currentRetryCount + 1 }
        : e
    ));

    // Wait with exponential backoff
    const delay = getRetryDelay(currentRetryCount);
    await new Promise(resolve => setTimeout(resolve, delay));

    return true;
  }, [errors]);

  /**
   * Get errors by category
   */
  const getErrorsByCategory = useCallback((category: ErrorCategory): ErrorDetails[] => {
    return errors.filter(error => error.category === category);
  }, [errors]);

  /**
   * Get errors by severity
   */
  const getErrorsBySeverity = useCallback((severity: ErrorSeverity): ErrorDetails[] => {
    return errors.filter(error => error.severity === severity);
  }, [errors]);

  const value: GlobalErrorContextType = {
    errors,
    reportError,
    clearError,
    clearAllErrors,
    retryError,
    getErrorsByCategory,
    getErrorsBySeverity,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};